import { FLAG_UNSET } from "@ext/properties/logic/grouping/groupArticles";
import {
	isHasValue,
	isManyProperty,
	type Property,
	type PropertyValue,
	type ViewRenderData,
	type ViewRenderGroup,
} from "@ext/properties/models";

export interface MoveCardParams {
	data: ViewRenderGroup[];
	columnID: number;
	cardID: number;
	newColumnID: number;
	groupbyProperty: string;
	defs?: PropertyValue[];
	isDelete?: boolean;
	selectedValue?: string;
	property?: Property;
}

export interface MoveCardResult {
	data: ViewRenderGroup[];
	newValue: string;
	previousValue: string | null;
	isDelete: boolean;
	isFilteredOut: boolean;
}

const isValueFilteredOut = (defs: PropertyValue[] | undefined, groupbyProperty: string, value: string): boolean => {
	if (!defs?.length || !value) return false;
	const def = defs.find((d) => d.id === groupbyProperty);

	if (!def?.value?.length) return false;
	return def.value.includes(value);
};

const moveCardBetweenColumns = (
	data: ViewRenderGroup[],
	sourceColumnIndex: number,
	cardIndex: number,
	targetColumnIndex: number,
	article: ViewRenderData,
): ViewRenderGroup[] => {
	return data.map((grp, index) => {
		if (index === sourceColumnIndex) {
			const subgroup = grp.subgroups![0];
			const articles = subgroup.articles.filter((_, i) => i !== cardIndex);
			return { ...grp, subgroups: [{ ...subgroup, articles }] };
		}

		if (index === targetColumnIndex) {
			const subgroup = grp.subgroups![0];
			const articles = subgroup.articles.some((a) => a.itemPath === article.itemPath)
				? subgroup.articles.map((a) => (a.itemPath === article.itemPath ? article : a))
				: [...subgroup.articles, article];
			return { ...grp, subgroups: [{ ...subgroup, articles }] };
		}

		return grp;
	});
};

const createColumnWithArticle = (name: string, groupBy: string, article: ViewRenderData): ViewRenderGroup => {
	return {
		group: [name],
		groupBy,
		articles: [article],
		subgroups: [{ group: [name], groupBy, articles: [article] }],
	};
};

const applyGroupbyValueToArticle = (
	sourceArticle: ViewRenderData,
	groupbyProperty: string,
	newValue: string,
	isDelete: boolean,
	previousValue: string | null,
	isMany: boolean,
): ViewRenderData => {
	const otherProps = sourceArticle.otherProps ?? [];
	const indexProperty = otherProps.findIndex((prop) => prop.name === groupbyProperty);
	const kept = isMany ? (otherProps[indexProperty]?.value ?? []).filter((v) => v !== previousValue) : [];
	const value = isDelete || !newValue || kept.includes(newValue) ? kept : [...kept, newValue];

	if (indexProperty === -1)
		return {
			...sourceArticle,
			otherProps: [...otherProps, { id: groupbyProperty, name: groupbyProperty, value } as Property],
		};

	return {
		...sourceArticle,
		otherProps: otherProps.map((prop, i) => (i === indexProperty ? { ...prop, value } : prop)),
	};
};

const moveCardInKanban = (params: MoveCardParams): MoveCardResult => {
	const { data, columnID, cardID, newColumnID, groupbyProperty, defs, isDelete, selectedValue, property } = params;

	const group = data[columnID]?.subgroups?.[0];
	const sourceArticle = group?.articles[cardID];
	if (!group || !sourceArticle)
		return { data, newValue: "", previousValue: null, isDelete: false, isFilteredOut: false };

	const previousValue = data[columnID]?.group?.[0] ?? null;

	const targetGroup = data[newColumnID];
	const holdsNoValue = property ? !isHasValue[property.type] : false;
	const targetKey = targetGroup?.group?.[0];
	const isEmptyColumn = newColumnID >= 0 && (targetKey === null || (holdsNoValue && targetKey === FLAG_UNSET));
	const newValue = targetGroup?.group?.[0] ?? (newColumnID < 0 ? selectedValue : undefined) ?? "";
	const filteredOut = isValueFilteredOut(defs, groupbyProperty, selectedValue ?? "");
	const shouldAddColumn = newColumnID >= 0 || (newColumnID < 0 && !!selectedValue && !filteredOut);
	const effectiveIsDelete = isDelete || isEmptyColumn || (newColumnID < 0 && !selectedValue);

	const shouldRemoveFromUI = (effectiveIsDelete && !isEmptyColumn) || filteredOut;
	const article = applyGroupbyValueToArticle(
		sourceArticle,
		groupbyProperty,
		newValue,
		effectiveIsDelete,
		previousValue,
		property ? !!isManyProperty[property.type] : false,
	);

	let newData: ViewRenderGroup[];
	if (shouldAddColumn && !shouldRemoveFromUI) {
		if (newColumnID >= 0 && newColumnID < data.length) {
			newData = moveCardBetweenColumns(data, columnID, cardID, newColumnID, article);
		} else if (newColumnID < 0) {
			const dataWithoutCard = data.map((grp, index) => {
				if (index !== columnID) return grp;
				const subgroup = grp.subgroups![0];
				const articles = subgroup.articles.filter((_, i) => i !== cardID);
				return { ...grp, subgroups: [{ ...subgroup, articles }] };
			});
			const newGroup = createColumnWithArticle(selectedValue!, groupbyProperty, article);
			newData = [...dataWithoutCard, newGroup];
		} else {
			newData = data.map((grp, index) => {
				if (index !== columnID) return grp;
				const subgroup = grp.subgroups![0];
				const articles = subgroup.articles.filter((_, i) => i !== cardID);
				return { ...grp, subgroups: [{ ...subgroup, articles }] };
			});
		}
	} else {
		newData = data.map((grp, index) => {
			if (index !== columnID) return grp;
			const subgroup = grp.subgroups![0];
			const articles = subgroup.articles.filter((_, i) => i !== cardID);

			return { ...grp, subgroups: [{ ...subgroup, articles }] };
		});
	}

	return {
		data: newData,
		newValue,
		previousValue,
		isDelete: effectiveIsDelete,
		isFilteredOut: filteredOut,
	};
};

export default moveCardInKanban;
